import { makeAutoObservable } from "mobx";
import BrandFontsStore, { IBrandFontsStore } from "../DesignEditor/brand-fonts-store.tsx";
import BrandsDefinitionStore, { IBrandsDefinitionStore } from "../DesignEditor/brands-definition-store.tsx";
import CampaignStore, { ICampaignStore } from "../DesignEditor/campaign-store.tsx";
import DesignEditorStore, { IDesignEditorStore } from "../DesignEditor/design-editor-store.tsx";
import ImagesStore, { IImagesStore } from "../DesignEditor/images-store.tsx";
import LogoStore, { ILogoStore } from "../DesignEditor/logo-store.tsx";
import AnalyticsStore, { IAnalyticsStore } from "../analytics-store.tsx";
import PlaygroundUserToOrgConversionStore, {
	IPlaygroundUserToOrgConversionStore,
} from "../components/Popups/PlaygroundUserToOrgConversionPopup/playground-user-to-org-conversion-store.tsx";
import PricingStore, { IPricingStore } from "../components/Popups/Pricing/store.tsx";
import AIEditorStore, { IAIEditorStore } from "../pages/AIEditor/ai-editor-store.tsx";
import ObjectsStore, { IObjectsStore } from "../pages/AIEditor/objects-store.tsx";
import AttributionReportsStore, {
	IAttributionReportsStore,
} from "../pages/Account/AttributionReports/attribution-store.ts";
import IpProjectsStore, { IIpProjectsStore } from "../pages/Account/IpProjects/ip-projects-store.tsx";
import AccountStore, { IAccountStore } from "../pages/Account/store.tsx";
import FiboGenerateStore, { IFiboGenerateStore } from "../pages/Fibo/fibo-generate-store.tsx";
import FiboStore, { IFiboStore } from "../pages/Fibo/fibo-store.tsx";
import FiboUsageStore, { IFiboUsageStore } from "../pages/Fibo/fibo-usage-store.tsx";
import { ITgStore, TgStore } from "../pages/NewTG/store/new-tg-stores.tsx";
import ImageToImageStore, { IImageToImageStore } from "../pages/Playground/ImageToImage/image-to-image-store.tsx";
import ProductPlacementStore, {
	IProductPlacementStore,
} from "../pages/Playground/ProductPlacement/product-placement-store.tsx";
import TextToImageStore, { ITextToImageStore } from "../pages/Playground/TextToImage/text-to-image-store.tsx";
import VideoGenerationStore, {
	IVideoGenerationStore,
} from "../pages/Playground/VideoGeneration/video-generation-store.ts";
import PlaygroundStore, { IPlaygroundStore } from "../pages/Playground/store/playground-store.tsx";
import { IVideoEditingStore, VideoEditingStore } from "../pages/VideoEditing/store/video-editing-store.ts";
import AuthStore, { IAuthStore } from "../pages/auth/store";
import AppStore, { IAppStore } from "../pages/store";
import TourStore, { ITourStore } from "../tours/tour-store.ts";
import UIStore, { IUIStore } from "../ui-store.tsx";
import AnonymousUsageStore, { IAnonymousUsageStore } from "./anonymous-usage-store.tsx";

export interface IRootStore {
	appStore: IAppStore;
	authStore: IAuthStore;
	analyticsStore: IAnalyticsStore;
	accountStore: IAccountStore;
	tgStore: ITgStore;
	aiEditorStore: IAIEditorStore;
	objectsStore: IObjectsStore;
	playgroundStore: IPlaygroundStore;
	textToImageStore: ITextToImageStore;
	productPlacementStore: IProductPlacementStore;
	imageToImageStore: IImageToImageStore;
	videoGenerationStore: IVideoGenerationStore;
	videoEditingStore: IVideoEditingStore;
	uiStore: IUIStore;
	tourStore: ITourStore;
	playgroundUserToOrgConversionStore: IPlaygroundUserToOrgConversionStore;
	campaignStore: ICampaignStore;
	imagesStore: IImagesStore;
	brandsDefinitionStore: IBrandsDefinitionStore;
	logoStore: ILogoStore;
	brandFontStore: IBrandFontsStore;
	designEditorStore: IDesignEditorStore;
	pricingStore: IPricingStore;
	attributionReportsStore: IAttributionReportsStore;
	anonymousUsageStore: IAnonymousUsageStore;
	fiboUsageStore: IFiboUsageStore;
	fiboStore: IFiboStore;
	fiboGenerateStore: IFiboGenerateStore;
	ipProjectsStore: IIpProjectsStore;
	resetStores: () => void;
}

export default class RootStore implements IRootStore {
	appStore = new AppStore(this) as IAppStore;
	authStore = new AuthStore(this) as IAuthStore;
	analyticsStore = new AnalyticsStore(this) as IAnalyticsStore;
	accountStore = new AccountStore() as IAccountStore;
	tgStore = new TgStore(this) as ITgStore;
	playgroundStore = new PlaygroundStore(this) as IPlaygroundStore;
	textToImageStore = new TextToImageStore(this) as ITextToImageStore;
	productPlacementStore = new ProductPlacementStore(this) as IProductPlacementStore;
	imageToImageStore = new ImageToImageStore(this) as IImageToImageStore;
	videoGenerationStore = new VideoGenerationStore(this) as IVideoGenerationStore;
	videoEditingStore = new VideoEditingStore(this) as IVideoEditingStore;
	aiEditorStore = new AIEditorStore() as IAIEditorStore;
	objectsStore = new ObjectsStore(this) as IObjectsStore;
	uiStore = new UIStore(this) as IUIStore;
	tourStore = new TourStore(this) as ITourStore;
	playgroundUserToOrgConversionStore = new PlaygroundUserToOrgConversionStore(
		this,
	) as IPlaygroundUserToOrgConversionStore;
	campaignStore = new CampaignStore(this) as ICampaignStore;
	brandsDefinitionStore = new BrandsDefinitionStore() as IBrandsDefinitionStore;
	imagesStore = new ImagesStore(this) as IImagesStore;
	logoStore = new LogoStore() as ILogoStore;
	brandFontStore = new BrandFontsStore() as IBrandFontsStore;
	designEditorStore = new DesignEditorStore(this) as IDesignEditorStore;
	pricingStore = new PricingStore(this) as IPricingStore;
	attributionReportsStore = new AttributionReportsStore(this) as IAttributionReportsStore;
	anonymousUsageStore = new AnonymousUsageStore() as IAnonymousUsageStore;
	fiboUsageStore = new FiboUsageStore(this) as IFiboUsageStore;
	fiboStore = new FiboStore(this) as IFiboStore;
	fiboGenerateStore = new FiboGenerateStore(this) as IFiboGenerateStore;
	ipProjectsStore = new IpProjectsStore() as IIpProjectsStore;

	constructor() {
		makeAutoObservable(this);
	}

	resetStores() {
		this.authStore.resetStore();
		this.attributionReportsStore.resetStore();
		this.pricingStore.reset();
		this.playgroundStore.clearResults();
		this.imageToImageStore.resetConfigChange();
		this.tgStore.tgDatasetStore.reset();
		this.tgStore.tgModelPlaygroundStore.resetPlayground();
		this.tgStore.tgProjectStore.reset();
		this.tgStore.tgModelStore.reset();
		this.accountStore.clearStoreData();
		this.playgroundUserToOrgConversionStore.resetStore();
		this.appStore.reset();
		this.fiboUsageStore.resetUsageEngagementPopupState();
		this.fiboGenerateStore.reset();
	}
}
